import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Satellite, RefreshCw, Radio, Cloud, Ruler, ImageIcon, ExternalLink, Timer, ShieldCheck, Sparkles } from 'lucide-react';
import { fmtDate, fmtRel } from '@/lib/format';
import { VerifiedBadge, QualityTierBadge } from '@/components/common/VerifiedBadge';

/**
 * SkyFiObservations
 *
 * Timeline of REAL satellite observations sourced via the SkyFi Platform API.
 * Non-blocking behaviour:
 *   - When observations are missing for this asset we call
 *     /api/imagery/bootstrap-status every ~6s and show a progress skeleton
 *     so the user sees the pipeline working (search → stitch → AI).
 *   - Manual re-trigger is available for org-admins via
 *     /api/imagery/refresh/{asset_id} (non-blocking; kicks off a task).
 */
export function SkyFiObservations({ asset }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [progress, setProgress] = useState(null);

  const load = useCallback(async () => {
    try {
      const [obsResp, statusResp, bsResp] = await Promise.all([
        api.get('/imagery/observations', { params: { asset_id: asset.id } }),
        api.get('/imagery/status'),
        api.get('/imagery/bootstrap-status').catch(() => ({ data: null })),
      ]);
      setItems(obsResp.data || []);
      setConfigured(!!statusResp.data?.configured);
      if (bsResp?.data) {
        const me = (bsResp.data.items || []).find((i) => i.asset_id === asset.id);
        setProgress(me || null);
      }
    } catch (e) {
      // silent — the tab still renders in an empty state
    } finally {
      setLoading(false);
    }
  }, [asset.id]);

  useEffect(() => { load(); }, [load]);

  // Poll bootstrap-status while there are 0 observations OR pipeline is running.
  useEffect(() => {
    if (!configured) return undefined;
    const shouldPoll = items.length < 2 || progress?.progress?.status && !['done', 'already_bootstrapped'].includes(progress?.progress?.status);
    if (!shouldPoll) return undefined;
    const t = setInterval(() => { load(); }, 6000);
    return () => clearInterval(t);
  }, [configured, items.length, progress?.progress?.status, load]);

  const refresh = async () => {
    setBusy(true);
    const t = toast.loading('Contacting SkyFi and running AI change-detection pipeline…');
    try {
      const { data } = await api.post(`/imagery/refresh/${asset.id}`, null, {
        params: { count: 2 },
      });
      toast.dismiss(t);
      toast.success(data?.message || 'Pipeline started. Observations will populate within ~1–2 minutes.');
      await load();
    } catch (e) {
      toast.dismiss(t);
      const msg = e?.response?.data?.detail || e.message || 'Fetch failed.';
      toast.error(`SkyFi trigger failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  const grouped = useMemo(() => {
    return [...items].sort((a, b) => (b.acquired_at || '').localeCompare(a.acquired_at || ''));
  }, [items]);

  const opened = grouped.find((o) => o.id === openId);

  const pipelineStatus = progress?.progress?.status;
  const pipelineAi = progress?.progress?.ai_status;
  const isPipelineActive = pipelineStatus && !['done', 'already_bootstrapped'].includes(pipelineStatus);

  return (
    <Card className="p-4" data-testid="skyfi-observations">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="rounded-lg bg-primary/10 text-primary p-2">
            <Satellite size={16} />
          </div>
          <div>
            <div className="font-semibold text-[15px] flex items-center gap-2">
              Real satellite observations
              {grouped.length > 0 && <VerifiedBadge label="Live SkyFi" />}
            </div>
            <div className="text-xs text-muted-foreground">
              Sentinel-1 SAR &amp; Sentinel-2 multispectral via <span className="font-medium">SkyFi Platform API</span> — Open Data
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!configured && (
            <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-700">
              SkyFi not configured
            </Badge>
          )}
          <Button
            size="sm"
            variant="default"
            onClick={refresh}
            disabled={busy || !configured}
            data-testid="skyfi-refresh-btn"
          >
            <RefreshCw size={14} className={`mr-1.5 ${busy ? 'animate-spin' : ''}`} />
            {busy ? 'Fetching…' : 'Re-run pipeline'}
          </Button>
        </div>
      </div>

      {/* Pipeline status strip */}
      {(isPipelineActive || (progress && grouped.length < 2)) && (
        <div className="mt-3 rounded-md border border-primary/30 bg-primary/5 text-primary p-2.5 flex items-center gap-2 text-[12px]" data-testid="skyfi-pipeline-status">
          <Sparkles size={14} className="animate-pulse" />
          <div className="flex-1">
            <div className="font-medium">Fetching latest imagery from SkyFi…</div>
            <div className="text-[11px] text-primary/80">
              {pipelineStatus === 'searching' && 'Searching the SkyFi archive catalog…'}
              {pipelineStatus === 'fetching' && `Stitching high-resolution AOI previews (${progress?.progress?.archives_picked || 0} archives selected)…`}
              {pipelineStatus === 'fetched' && 'Preparing to run AI change-detection…'}
              {pipelineStatus === 'ai_starting' && 'Running CV + Gemini vision pipeline on the newest before/after pair…'}
              {pipelineAi === 'cv_running' && 'Computing change mask with OpenCV…'}
              {pipelineAi === 'llm_running' && 'Interpreting change with Gemini 2.5 Pro…'}
              {!pipelineStatus && 'The platform is populating real observations for this asset. This usually takes 1–2 minutes on first boot.'}
            </div>
          </div>
        </div>
      )}

      <div className="mt-4">
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : grouped.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border rounded-md">
            {isPipelineActive ? (
              <>
                <Skeleton className="h-4 w-3/4 mx-auto mb-2" />
                <Skeleton className="h-4 w-1/2 mx-auto" />
              </>
            ) : (
              <>
                No real satellite observations ingested yet for this asset.
                <div className="mt-1 text-xs">Click <b>Re-run pipeline</b> above to pull recent Sentinel-1/-2 imagery from SkyFi and run change detection.</div>
              </>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {grouped.map((o) => (
              <li key={o.id} className="py-3 flex items-center gap-3" data-testid={`skyfi-obs-row-${o.id}`}>
                <div
                  className={`rounded-md p-2 shrink-0 ${o.product_type === 'SAR' ? 'bg-blue-500/10 text-blue-600' : 'bg-emerald-500/10 text-emerald-600'}`}
                >
                  {o.product_type === 'SAR' ? <Radio size={16} /> : <ImageIcon size={16} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="font-medium text-[14px] truncate">
                      {o.product_type === 'SAR' ? 'Synthetic Aperture Radar' : 'Multispectral optical'}
                      <span className="text-muted-foreground font-normal"> · {o.provider}</span>
                    </div>
                    <QualityTierBadge tier={o.quality_tier || 'preview'} />
                    <Badge variant="outline" className="text-[10px]">{o.quality}</Badge>
                    {o.product_type !== 'SAR' && (
                      <Badge variant="outline" className="text-[10px]">
                        <Cloud size={10} className="mr-1" />
                        {(o.cloud_coverage_pct || 0).toFixed(1)}% cloud
                      </Badge>
                    )}
                    {o.order_id && o.quality_tier !== 'full' && (
                      <Badge variant="outline" className="text-[10px] border-primary/40 text-primary">
                        Order {String(o.order_status || 'PENDING').replace('_', ' ')}
                      </Badge>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><Timer size={10} /> {fmtDate(o.acquired_at)} · {fmtRel(o.acquired_at)}</span>
                    <span className="inline-flex items-center gap-1"><Ruler size={10} /> ~10 m/px (Sentinel native)</span>
                    <span className="inline-flex items-center gap-1 font-mono">Archive {o.provider_archive_id?.slice(0, 8)}…</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {o.usable && (
                    <Button size="sm" variant="outline" onClick={() => setOpenId(o.id)} data-testid={`skyfi-view-${o.id}`}>
                      View AOI
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {progress?.real_detections > 0 && (
        <div className="mt-4 rounded-md border border-emerald-500/30 bg-emerald-500/5 text-emerald-800 dark:text-emerald-200 p-2.5 flex items-center gap-2 text-[12px]" data-testid="skyfi-ai-summary">
          <ShieldCheck size={14} />
          <span>
            <strong>{progress.real_detections}</strong> AI-verified detection{progress.real_detections === 1 ? '' : 's'} generated from real satellite imagery for this asset.
            Look for the <VerifiedBadge label="Verified" /> label in Alerts and Detections.
          </span>
        </div>
      )}

      {/* AOI Preview dialog */}
      <Dialog open={!!openId} onOpenChange={(v) => !v && setOpenId(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Satellite size={16} className="text-primary" />
              {opened ? `${opened.product_type === 'SAR' ? 'SAR' : 'Optical'} · ${opened.provider}` : 'Observation'}
              {opened && <QualityTierBadge tier={opened.quality_tier || 'preview'} />}
            </DialogTitle>
          </DialogHeader>
          {opened && <AoiPreview obsId={opened.id} obs={opened} />}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function AoiPreview({ obsId, obs }) {
  const [b64, setB64] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/observations/${obsId}`);
        setB64(data.image_b64 || null);
      } catch (e) {
        // silent
      } finally {
        setLoading(false);
      }
    })();
  }, [obsId]);
  return (
    <div>
      <div className="rounded-lg overflow-hidden border border-border bg-secondary/30 flex items-center justify-center min-h-[320px]">
        {loading ? (
          <Skeleton className="w-full h-[400px]" />
        ) : b64 ? (
          <img
            src={`data:${obs.image_mime || 'image/jpeg'};base64,${b64}`}
            alt={`Aakash Drishti AOI observation ${obs.provider_archive_id}`}
            className="w-full h-auto"
          />
        ) : (
          <div className="text-sm text-muted-foreground py-10">Image not available.</div>
        )}
      </div>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px]">
        <Info label="Capture time" value={fmtDate(obs.acquired_at)} />
        <Info label="Provider" value={obs.provider} />
        <Info label="Product" value={obs.product_type} />
        <Info label="Cloud cover" value={obs.product_type === 'SAR' ? '—' : `${(obs.cloud_coverage_pct || 0).toFixed(1)}%`} />
      </div>
      <div className="mt-3 text-[11px] text-muted-foreground italic flex items-start gap-2">
        <ExternalLink size={11} className="mt-0.5 shrink-0" />
        <span>
          Real satellite observation retrieved from SkyFi ({obs.provider}). Archive {obs.provider_archive_id?.slice(0, 12)}…
          Rendered from the operator&apos;s XYZ tile server at native Sentinel resolution (~10&nbsp;m / pixel).
        </span>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-md border border-border p-2">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
      <div className="mt-0.5 truncate">{value || '—'}</div>
    </div>
  );
}
